import React, { useState } from "react";
import { Download, StickyNote } from "lucide-react";
import { inRange, timeAgo } from "./shared";

const COLUMNS = ["New", "Contacted", "Goal Review Booked", "Joined", "Lost"];
const COL_COLOR = { New: "#E56A79", Contacted: "#E8C766", "Goal Review Booked": "#7DB8E8", Joined: "#34D399", Lost: "#666" };

function LeadCard({ lead, onStatus, onNotes }) {
  const [showNotes, setShowNotes] = useState(false);
  const [note, setNote] = useState(lead.notes || "");
  const isStaleNew = (lead.status || "New") === "New" && Date.now() - new Date(lead.created_at).getTime() > 2 * 3600e3;
  const source = [lead.utm_source, lead.utm_medium].filter(Boolean).join(" / ") || (lead.gclid ? "google ads" : lead.referrer ? "referral" : "direct");

  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/plain", lead.id)}
      data-testid={`pipeline-card-${lead.id}`}
      className="bg-[#121214] border border-white/8 hover:border-white/25 p-3.5 cursor-grab active:cursor-grabbing transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[13px] font-semibold text-white leading-tight">{lead.name}</span>
        {isStaleNew && <span className="shrink-0 bg-[#9D0423] text-white font-mono text-[8px] uppercase tracking-[0.1em] px-1.5 py-0.5">Not contacted</span>}
      </div>
      <div className="mt-1.5 text-[11px] text-neutral-400 leading-snug">{lead.program_interest || "—"}</div>
      <div className="mt-1 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.08em] text-neutral-600">
        <span className="truncate max-w-[120px]" title={source}>{source}</span>
        <span>{timeAgo(lead.created_at)}</span>
      </div>
      <div className="mt-2.5 flex items-center gap-1.5">
        <select
          value={lead.status || "New"}
          onChange={(e) => onStatus(lead.id, e.target.value)}
          data-testid={`pipeline-status-${lead.id}`}
          className="flex-1 bg-[#0A0A0B] border border-white/15 text-white text-[10px] px-1.5 py-1 focus:outline-none focus:border-[#9D0423]"
        >
          {COLUMNS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button
          onClick={() => setShowNotes(!showNotes)}
          data-testid={`pipeline-notes-toggle-${lead.id}`}
          title="Notes"
          className={`p-1.5 border transition-colors ${lead.notes ? "border-[#E8C766]/50 text-[#E8C766]" : "border-white/15 text-neutral-500 hover:text-white"}`}
        >
          <StickyNote size={11} />
        </button>
      </div>
      {showNotes && (
        <div className="mt-2">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Follow-up notes…"
            data-testid={`pipeline-notes-input-${lead.id}`}
            className="w-full bg-[#0A0A0B] border border-white/15 focus:border-[#9D0423] focus:outline-none text-white text-[11px] px-2 py-1.5"
          />
          <button
            onClick={() => { onNotes(lead.id, note); setShowNotes(false); }}
            data-testid={`pipeline-notes-save-${lead.id}`}
            className="mt-1 w-full font-mono text-[9px] uppercase tracking-[0.12em] text-white bg-[#9D0423] hover:bg-[#7E031C] py-1.5 transition-colors"
          >
            Save note
          </button>
        </div>
      )}
    </div>
  );
}

export default function Pipeline({ leads, range, onStatus, onNotes, toast }) {
  const [dragOver, setDragOver] = useState(null);
  const filtered = leads.filter((l) => !l.suspected_bot && inRange(l.created_at, range));

  const exportCsv = () => {
    if (!filtered.length) return toast({ title: "Nothing to export", description: "No leads in this range." });
    const cols = [["Name", "name"], ["Email", "email"], ["Phone", "phone"], ["Program", "program_interest"], ["Status", "status"], ["Notes", "notes"], ["Form", "form_name"], ["UTM Source", "utm_source"], ["UTM Medium", "utm_medium"], ["UTM Campaign", "utm_campaign"], ["GCLID", "gclid"], ["Referrer", "referrer"], ["Contacted At", "contacted_at"], ["Received", "created_at"]];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.map(([l]) => esc(l)).join(","), ...filtered.map((r) => cols.map(([, k]) => esc(r[k])).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    a.download = `irongrit-pipeline-${range}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast({ title: "Export ready", description: `${filtered.length} leads downloaded.` });
  };

  const drop = (e, status) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    const lead = filtered.find((l) => l.id === id);
    if (lead && (lead.status || "New") !== status) onStatus(id, status);
  };

  return (
    <div className="mt-6" data-testid="pipeline-tab">
      <div className="flex items-center justify-between gap-4 mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">
          {filtered.length} leads in range · drag cards or use the dropdown to move them
        </p>
        <button
          onClick={exportCsv}
          data-testid="pipeline-export-csv"
          className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-neutral-300 hover:text-white font-mono text-[10px] uppercase tracking-[0.14em] px-3.5 py-2 transition-colors"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1">
        {COLUMNS.map((col) => {
          const cards = filtered.filter((l) => (l.status || "New") === col);
          return (
            <div
              key={col}
              onDragOver={(e) => { e.preventDefault(); setDragOver(col); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => drop(e, col)}
              data-testid={`pipeline-col-${col.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex-1 min-w-[230px] bg-[#0D0D0F] border p-3 transition-colors ${dragOver === col ? "border-[#9D0423]" : "border-white/5"}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.16em]" style={{ color: COL_COLOR[col] }}>{col}</span>
                <span className="font-heading font-extrabold text-sm text-neutral-400">{cards.length}</span>
              </div>
              <div className="space-y-2.5 min-h-[60px]">
                {cards.map((l) => (
                  <LeadCard key={l.id} lead={l} onStatus={onStatus} onNotes={onNotes} />
                ))}
                {cards.length === 0 && <div className="border border-dashed border-white/8 p-4 text-center font-mono text-[9px] uppercase tracking-[0.12em] text-neutral-700">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
